/**
 * Questionnaire Form Component
 *
 * Dynamic form for capturing requirement details.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { RequirementType } from '../types';
import clsx from 'clsx';

interface FormData {
  title: string;
  description: string;
  priority: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  category?: string;
}

interface QuestionnaireFormProps {
  type: RequirementType;
  categories?: string[];
  onSubmit: (data: FormData) => void;
  onBack: () => void;
  isSubmitting: boolean;
  /** Pre-populate form with saved draft data */
  initialData?: FormData | null;
  /** Called whenever form data changes so parent can persist drafts */
  onFormDataChange?: (data: FormData) => void;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Minor issue, no impact on work' },
  { value: 'medium', label: 'Medium', description: 'Noticeable issue, workaround available' },
  { value: 'high', label: 'High', description: 'Significant impact, needs attention soon' },
  { value: 'critical', label: 'Critical', description: 'Blocking work, needs immediate attention' },
];

const TYPE_LABELS: Record<RequirementType, string> = {
  bug_fix: 'Bug Report',
  new_feature: 'New Feature',
  change_request: 'Change Request',
  enhancement: 'Enhancement',
  performance: 'Performance Issue',
  usability: 'Usability Feedback',
  documentation: 'Documentation',
  info_request: 'Question',
};

export function QuestionnaireForm({
  type,
  categories = [],
  onSubmit,
  onBack,
  isSubmitting,
  initialData,
  onFormDataChange,
}: QuestionnaireFormProps) {
  const [formData, setFormData] = useState<FormData>(() =>
    initialData ?? {
      title: '',
      description: '',
      priority: 'medium',
      stepsToReproduce: '',
      expectedBehavior: '',
      actualBehavior: '',
      category: categories[0] || '',
    }
  );
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Sync form data changes to parent for draft persistence
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  const handleChange = useCallback(
    (field: keyof FormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
        // Clear error when user starts typing
        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      },
    [errors]
  );

  const validate = useCallback(() => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Please provide more detail (at least 20 characters)';
    }

    // For bugs, require reproduction steps
    if (type === 'bug_fix' && !formData.stepsToReproduce?.trim()) {
      newErrors.stepsToReproduce = 'Steps to reproduce are required for bug reports';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, type]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (validate()) {
        onSubmit(formData);
      }
    },
    [formData, validate, onSubmit]
  );

  const isBugReport = type === 'bug_fix' || type === 'performance';

  return (
    <form onSubmit={handleSubmit} className="psc-questionnaire">
      <div className="psc-questionnaire__header">
        <span className="psc-questionnaire__type-badge">{TYPE_LABELS[type]}</span>
      </div>

      {/* Title */}
      <div className="psc-form-field">
        <label htmlFor="psc-title" className="psc-form-field__label">
          Title <span className="psc-form-field__required">*</span>
        </label>
        <input
          id="psc-title"
          type="text"
          value={formData.title}
          onChange={handleChange('title')}
          placeholder="Brief summary of your request"
          className={clsx('psc-form-field__input', errors.title && 'psc-form-field__input--error')}
          disabled={isSubmitting}
          data-testid="psc-title-input"
        />
        {errors.title && <span className="psc-form-field__error">{errors.title}</span>}
      </div>

      {/* Category (if available) */}
      {categories.length > 0 && (
        <div className="psc-form-field">
          <label htmlFor="psc-category" className="psc-form-field__label">
            Category
          </label>
          <select
            id="psc-category"
            value={formData.category}
            onChange={handleChange('category')}
            className="psc-form-field__select"
            disabled={isSubmitting}
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Description */}
      <div className="psc-form-field">
        <label htmlFor="psc-description" className="psc-form-field__label">
          Description <span className="psc-form-field__required">*</span>
        </label>
        <textarea
          id="psc-description"
          value={formData.description}
          onChange={handleChange('description')}
          placeholder={
            isBugReport
              ? 'Describe the issue in detail. What were you trying to do?'
              : 'Describe your request in detail. What problem does it solve?'
          }
          className={clsx(
            'psc-form-field__textarea',
            errors.description && 'psc-form-field__textarea--error'
          )}
          rows={4}
          disabled={isSubmitting}
          data-testid="psc-description-input"
        />
        {errors.description && <span className="psc-form-field__error">{errors.description}</span>}
      </div>

      {/* Bug-specific fields */}
      {isBugReport && (
        <>
          <div className="psc-form-field">
            <label htmlFor="psc-steps" className="psc-form-field__label">
              Steps to Reproduce {type === 'bug_fix' && <span className="psc-form-field__required">*</span>}
            </label>
            <textarea
              id="psc-steps"
              value={formData.stepsToReproduce}
              onChange={handleChange('stepsToReproduce')}
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
              className={clsx(
                'psc-form-field__textarea',
                errors.stepsToReproduce && 'psc-form-field__textarea--error'
              )}
              rows={3}
              disabled={isSubmitting}
            />
            {errors.stepsToReproduce && (
              <span className="psc-form-field__error">{errors.stepsToReproduce}</span>
            )}
          </div>

          <div className="psc-form-field">
            <label htmlFor="psc-expected" className="psc-form-field__label">
              Expected Behavior
            </label>
            <textarea
              id="psc-expected"
              value={formData.expectedBehavior}
              onChange={handleChange('expectedBehavior')}
              placeholder="What should happen?"
              className="psc-form-field__textarea"
              rows={2}
              disabled={isSubmitting}
            />
          </div>

          <div className="psc-form-field">
            <label htmlFor="psc-actual" className="psc-form-field__label">
              Actual Behavior
            </label>
            <textarea
              id="psc-actual"
              value={formData.actualBehavior}
              onChange={handleChange('actualBehavior')}
              placeholder="What actually happens?"
              className="psc-form-field__textarea"
              rows={2}
              disabled={isSubmitting}
            />
          </div>
        </>
      )}

      {/* Priority */}
      <div className="psc-form-field">
        <label className="psc-form-field__label">Priority</label>
        <div className="psc-priority-options">
          {PRIORITY_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={clsx(
                'psc-priority-option',
                `psc-priority-option--${option.value}`,
                formData.priority === option.value && 'psc-priority-option--selected'
              )}
            >
              <input
                type="radio"
                name="priority"
                value={option.value}
                checked={formData.priority === option.value}
                onChange={handleChange('priority')}
                className="psc-priority-option__input"
                disabled={isSubmitting}
              />
              <span className="psc-priority-option__label">{option.label}</span>
              <span className="psc-priority-option__description">{option.description}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="psc-form-actions">
        <button
          type="button"
          onClick={onBack}
          className="psc-button psc-button--secondary"
          disabled={isSubmitting}
        >
          Back
        </button>
        <button
          type="submit"
          className="psc-button psc-button--primary"
          disabled={isSubmitting}
          data-testid="psc-submit-button"
        >
          {isSubmitting ? 'Submitting...' : 'Review & Submit'}
        </button>
      </div>
    </form>
  );
}
